import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { AppShell } from "../../components/Sidebar";
import { useAuth } from "../../context/AuthContext";
import { OTPInput } from "../../components/OTPInput";
import { api } from "../../lib/api";
import { ArrowLeft, CheckCircle2, Loader2, Phone, AlertCircle } from "lucide-react";
import { toast } from "sonner";

export default function ChangeMobile() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState("enter");
  const [newMobile, setNewMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);

  const sendOtp = async (e) => {
    e?.preventDefault();
    if (newMobile.replace(/\D/g, "").length < 10) return toast.error("Enter a valid mobile number");
    setLoading(true);
    try { await api.post("/account/send-otp"); toast.success("OTP sent (demo: 123456)"); setStep("otp"); }
    finally { setLoading(false); }
  };

  const submit = async (e) => {
    e?.preventDefault();
    setLoading(true);
    try {
      setStep("updating");
      await api.post("/account/change-mobile", { new_mobile: newMobile, otp });
      await refreshUser();
      setStep("success");
    } catch { setStep("invalid"); }
    finally { setLoading(false); }
  };

  return (
    <AppShell role="teacher">
      <Link to="/teacher/settings" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700">
        <ArrowLeft size={14} /> Back to Settings
      </Link>
      <h1 className="mt-2 text-3xl font-extrabold tracking-tight text-slate-900">Change Mobile Number</h1>
      <p className="mt-1 text-slate-500">Update the mobile number associated with your account.</p>

      <div className="mt-6 max-w-lg rounded-2xl border border-slate-100 bg-white p-6 shadow-card">
        {step === "enter" && (
          <form onSubmit={sendOtp} className="space-y-4">
            <Field label="Current Mobile Number">
              <input value={user?.mobile || ""} disabled className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-500" />
            </Field>
            <Field label="New Mobile Number">
              <input data-testid="new-mobile" value={newMobile} onChange={(e) => setNewMobile(e.target.value)} placeholder="+91 98765 43210" className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100" required />
            </Field>
            <button data-testid="send-mobile-otp" disabled={loading} className="w-full rounded-xl bg-indigo-600 py-3 font-semibold text-white shadow hover:bg-indigo-700 disabled:opacity-60">{loading ? "Sending..." : "Send OTP"}</button>
          </form>
        )}

        {step === "otp" && (
          <form onSubmit={submit} className="space-y-5">
            <div className="flex items-center gap-3 rounded-xl bg-violet-50 p-3 text-sm text-violet-700">
              <Phone size={18} /> An OTP was sent to <span className="font-semibold">{newMobile}</span> (demo: 123456)
            </div>
            <OTPInput value={otp} onChange={setOtp} length={6} />
            <button data-testid="verify-mobile-otp" disabled={otp.length !== 6 || loading} className="w-full rounded-xl bg-indigo-600 py-3 font-semibold text-white shadow hover:bg-indigo-700 disabled:opacity-60">Verify &amp; Update</button>
          </form>
        )}

        {step === "invalid" && (
          <div className="flex flex-col items-center py-6">
            <div className="rounded-full bg-rose-100 p-4 text-rose-600"><AlertCircle size={36} /></div>
            <h2 className="mt-4 text-xl font-bold text-rose-700">Invalid OTP</h2>
            <p className="mt-1 text-sm text-slate-500">The code you entered is invalid. Please try again.</p>
            <button onClick={() => setStep("otp")} className="mt-5 rounded-xl bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white shadow hover:bg-indigo-700">Try Again</button>
          </div>
        )}

        {step === "updating" && (
          <div className="flex flex-col items-center py-10">
            <Loader2 className="animate-spin text-indigo-600" size={40} />
            <p className="mt-4 text-sm text-slate-600">Updating mobile number…</p>
          </div>
        )}

        {step === "success" && (
          <div className="flex flex-col items-center py-6">
            <div className="rounded-full bg-emerald-100 p-4 text-emerald-600"><CheckCircle2 size={40} /></div>
            <h2 className="mt-4 text-xl font-bold text-emerald-700">Mobile Number Updated!</h2>
            <p className="mt-1 text-center text-sm text-slate-500">Your new mobile is <span className="font-semibold">{newMobile}</span>.</p>
            <button onClick={() => navigate("/teacher/settings")} data-testid="back-to-profile-mobile" className="mt-5 rounded-xl bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white shadow hover:bg-indigo-700">Back to Profile</button>
          </div>
        )}
      </div>
    </AppShell>
  );
}

const Field = ({ label, children }) => (
  <label className="block"><span className="mb-1.5 block text-sm font-medium text-slate-700">{label}</span>{children}</label>
);
